import { useEffect, useRef } from 'react';

/** Only restore a saved position if it was captured within this window. */
const TTL_MS = 30 * 60 * 1000; // 30 minutes

interface SavedScroll {
  y: number;
  ts: number;
}

/**
 * Persist and restore the window scroll position for a list page so users don't
 * lose their place when they navigate away (e.g. into a venue detail page or
 * another section) and come back a short time later.
 *
 * The position is stored in `sessionStorage` (scoped to the tab session) keyed
 * by `key`, together with a timestamp. On return we only restore when the saved
 * offset is still fresh (<= {@link TTL_MS}); otherwise we leave the page at the
 * top. Storage failures (private mode / quota) are swallowed — scroll memory is
 * a nicety, never load-bearing.
 *
 * @param key   stable identifier for the page (usually the pathname). Separate
 *              keys keep e.g. /events and /events/calendar independent.
 * @param ready whether the page's main content has rendered, so the saved
 *              offset is actually reachable. Restore is attempted the first time
 *              `ready` becomes true.
 */
export function useScrollRestoration(key: string, ready: boolean): void {
  const storageKey = `mh:scroll:${key}`;
  // Tracks which storageKey we've already restored for, so switching keys on a
  // persistent component instance (e.g. /events ↔ /events/calendar) re-restores,
  // while repeated renders of the same key do not fight the user's scrolling.
  const restoredForKeyRef = useRef<string | null>(null);

  // Save on scroll (time-throttled) and again on unmount / navigate-away.
  useEffect(() => {
    let lastWrite = 0;
    let pending: number | undefined;

    const save = () => {
      pending = undefined;
      lastWrite = Date.now();
      try {
        const value: SavedScroll = { y: window.scrollY, ts: Date.now() };
        sessionStorage.setItem(storageKey, JSON.stringify(value));
      } catch {
        /* ignore storage errors (private mode / quota) */
      }
    };

    const onScroll = () => {
      if (pending !== undefined) return;
      const elapsed = Date.now() - lastWrite;
      if (elapsed >= 200) {
        save();
      } else {
        pending = window.setTimeout(save, 200 - elapsed);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (pending !== undefined) window.clearTimeout(pending);
      save(); // capture the final position when leaving the page
    };
  }, [storageKey]);

  // Restore once content is ready — only once per storageKey.
  useEffect(() => {
    if (!ready || restoredForKeyRef.current === storageKey) return;
    restoredForKeyRef.current = storageKey;

    let saved: SavedScroll | null = null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) saved = JSON.parse(raw) as SavedScroll;
    } catch {
      /* ignore malformed / unavailable storage */
    }

    if (saved && saved.y > 0 && Date.now() - saved.ts <= TTL_MS) {
      // Defer a frame so the freshly rendered list has its full height before
      // we scroll, otherwise the target offset may be clamped.
      window.requestAnimationFrame(() => window.scrollTo(0, saved!.y));
    }
  }, [ready, storageKey]);
}
